import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Modal from '../components/Modal';
import { useResusContext } from '../Resus/ResusContext';
import { getHospitalData } from './PerHospitalResusConfig';
import { useTranslation } from 'react-i18next';

const CheckboxContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  text-align: right;
  max-height: 400px;
  overflow-y: auto;
`;

const SectionTitle = styled.div`
  font-weight: bold;
  margin-top: 1rem;
  margin-bottom: 0.5rem;
  color: #103C6E;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  padding: 0.25rem 0;

  &.disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

interface ProtocolsDialogProps {
  isOpen: boolean;
  onClose: () => void;  
  protocols: string[];
  onUpdate: (protocols: string[]) => void;
}

const ProtocolsDialog: React.FC<ProtocolsDialogProps> = ({
  isOpen,
  onClose,
  protocols,
  onUpdate
}) => {
  const [selectedProtocols, setSelectedProtocols] = useState<string[]>([]);
  const { hospital } = useResusContext();
  const { t } = useTranslation(['resus']);
  const emergencyProtocols = getHospitalData(hospital).protocols;
  
  useEffect(() => {
    if (isOpen) {
      setSelectedProtocols([...protocols]);
    }
  }, [protocols, isOpen]);

  const handleCheckboxChange = (protocolId: string) => {
    setSelectedProtocols(prev => {
      if (prev.includes(protocolId)) {
        return prev.filter(id => id !== protocolId);
      } else {
        return [...prev, protocolId];
      }
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={onClose}
      title={t('protocols.chooseProtocols')}
      primaryButton={{
        text: t('buttons.update'),
        onClick: () => onUpdate(selectedProtocols)
      }}
      secondaryButton={{
        text: t('buttons.cancel'),
        onClick: onClose
      }}
    >
      <CheckboxContainer>
        {emergencyProtocols.emergencyProtocols.map((section) => (
          <div key={section.section}>
            <SectionTitle>{section.section}</SectionTitle>
            {section.protocols.map((protocol) => {
              return (
                <CheckboxLabel key={protocol.id}>
                  <input
                    type="checkbox"
                    checked={selectedProtocols.includes(protocol.id)}
                    onChange={() => handleCheckboxChange(protocol.id)}
                    data-testid={`protocols-dialog-${protocol.id}`}
                  />
                  {protocol.name}                  
                </CheckboxLabel>
              );
            })}
          </div>
        ))}
      </CheckboxContainer>
    </Modal>
  );
};

export default ProtocolsDialog;